/**
 * Managed card template renderer.
 *
 * Fills data-element slots in a pre-built HTML template with Algolia hit data,
 * applying the content mapping and style variables saved in the card_template
 * view setting.
 *
 * Custom template path (old {hit.field} style) is a separate concern — see
 * algolia-hits-component.js for the branch point.
 */

import { __ } from './i18n.js';

/**
 * Returns an SVG <use> string referencing the TUI sprite (inlined in wp_footer).
 * Icon names follow the "category/slug" convention, e.g. "system/check".
 * Symbol IDs in the sprite are "tui-{category}-{slug}".
 */
function iconToSvg( name ) {
	if ( ! name ) return '';
	const symbolId = 'tui-' + name.replace( '/', '-' );
	return `<svg class="tgbl-meta-icon" aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24"><use href="#${ symbolId }"></use></svg>`;
}

const templateCache = new Map();

const CSS_VAR_MAP = {
	bg_color:            '--tgbl-card-bg',
	accent_color:        '--tgbl-card-accent',
	accent_light_color:  '--tgbl-card-accent-light',
	text_color:          '--tgbl-card-text',
	text_emphasis_color: '--tgbl-card-text-emphasis',
	text_on_accent:      '--tgbl-card-text-on-accent',
	font_heading:        '--tgbl-card-font-heading',
	font_normal:         '--tgbl-card-font-normal',
	font_small:          '--tgbl-card-font-small',
	shadow_x:            '--tgbl-card-shadow-x',
	shadow_y:            '--tgbl-card-shadow-y',
	shadow_blur:         '--tgbl-card-shadow-blur',
	shadow_spread:       '--tgbl-card-shadow-spread',
	shadow_color:        '--tgbl-card-shadow-color',
};

/**
 * Returns true when a toggle value is on.
 * Defaults to true when the key has never been set (first load shows all slots).
 */
function toggleOn( content, key ) {
	return key in content ? Boolean( content[ key ] ) : true;
}

/**
 * Parses and caches the template HTML. Returns the root element.
 */
function getTemplateRoot( templateHtml ) {
	if ( ! templateCache.has( templateHtml ) ) {
		const div       = document.createElement( 'div' );
		div.innerHTML   = templateHtml;
		templateCache.set( templateHtml, div.firstElementChild );
	}
	return templateCache.get( templateHtml );
}

const DIMENSION_KEYS = new Set( [
	'font_heading', 'font_normal', 'font_small',
	'shadow_x', 'shadow_y', 'shadow_blur', 'shadow_spread',
] );

function resolveDimensionValue( v ) {
	try {
		const parsed = JSON.parse( v );
		if ( parsed !== null && typeof parsed === 'object' && 'value' in parsed ) {
			return `${ parsed.value }${ parsed.unit }`;
		}
	} catch {}
	const n = parseFloat( v );
	return isNaN( n ) ? v : `${ n }px`;
}

/**
 * Builds an inline style string of CSS custom properties from the styles object.
 */
function buildStyleAttr( styles ) {
	return Object.entries( CSS_VAR_MAP )
		.filter( ( [ key ] ) => styles[ key ] )
		.map( ( [ key, varName ] ) => {
			const val = DIMENSION_KEYS.has( key )
				? resolveDimensionValue( styles[ key ] )
				: styles[ key ];
			return `${ varName }: ${ val }`;
		} )
		.join( '; ' );
}

/**
 * Fills the cloned card DOM with hit data according to the content mapping.
 */
function fillCard( card, hit, content, facetMappingsByAttribute = {} ) {
	card.querySelectorAll( '[data-element]' ).forEach( el => {
		const id  = el.dataset.element;
		const tag = el.tagName.toLowerCase();

		if ( id === 'root' ) return;

		const isOn = toggleOn( content, id + '_toggle' );

		if ( ! isOn ) {
			el.style.display = 'none';
			return;
		}

		// Container elements (those with child data-element slots) only need
		// toggle handling — their children populate themselves.
		// Exception: ul/ol containers with a _datapoint are "dynamic list" elements
		// (e.g. tag cloud) — populate their <li> children from the hit array value.
		if ( el.querySelector( '[data-element]' ) ) {
			if ( tag === 'ul' || tag === 'ol' ) {
				const datapoint = content[ id + '_datapoint' ] || '';
				if ( datapoint && hit[ datapoint ] !== undefined ) {
					const rawVal = hit[ datapoint ];
					const values = Array.isArray( rawVal )
						? rawVal.map( v => String( v ) ).filter( Boolean )
						: [];
					if ( values.length ) {
						const liTemplate = el.querySelector( 'li' );
						const liClass    = liTemplate ? liTemplate.className : '';
						Array.from( el.querySelectorAll( 'li' ) ).forEach( li => li.remove() );
						values.forEach( val => {
							const li       = document.createElement( 'li' );
							li.className   = liClass;
							li.textContent = val;
							el.appendChild( li );
						} );
					}
				}
			}
			return;
		}

		let datapoint = content[ id + '_datapoint' ] || '';

		// Img / li elements: fall back to parent container's datapoint key for
		// configs saved before the key was corrected to use the child's own id.
		if ( ! datapoint && ( tag === 'img' || tag === 'li' ) ) {
			const parentId = el.parentElement?.closest( '[data-element]' )?.dataset.element;
			if ( parentId ) datapoint = content[ parentId + '_datapoint' ] || '';
		}

		let value;
		if ( datapoint === 'custom' ) {
			let parts;
			try { parts = JSON.parse( content[ id + '_custom_content' ] || '[]' ); } catch { parts = []; }
			if ( ! Array.isArray( parts ) ) parts = [];
			value = parts.map( p => {
				if ( p.type === 'text' ) return p.value || '';
				if ( ! p.field ) return '';
				const raw    = String( hit[ p.field ] ?? '' );
				const fMap   = facetMappingsByAttribute[ p.field ];
				return ( fMap && fMap[ raw ] != null ) ? fMap[ raw ] : raw;
			} ).join( '' );
		} else {
			const rawValue = datapoint ? String( hit[ datapoint ] ?? '' ) : '';
			const attrMap  = datapoint ? facetMappingsByAttribute[ datapoint ] : null;
			value          = ( attrMap && attrMap[ rawValue ] != null ) ? attrMap[ rawValue ] : rawValue;
		}

		if ( tag === 'img' ) {
			const useStatic = ( content[ 'hero_image_radio_button' ] || 'indexed_data' ) === 'static_image';
			const src       = useStatic
				? ( content[ 'hero_fallback_url' ] || '' )
				: ( value || content[ 'hero_fallback_url' ] || '' );
			el.setAttribute( 'src', src );

		} else if ( tag === 'button' ) {
			// Convert to an anchor for proper link behaviour.
			// Saved labels arrive already translated (server-side, via the
			// translation provider); only the default falls back to gettext here.
			const label   = content[ id + '_label' ] || __( 'Learn More', 'tangible-searchsync' );
			const linkKey = content[ id + '_link' ] || 'post_link';
			// 'post_link' is the sentinel for the default permalink fallback chain.
			// Any other value is treated as a hit attribute key.
			const postLink = linkKey === 'post_link'
				? ( hit.post_link || hit.permalink || hit.url || '#' )
				: ( String( hit[ linkKey ] ?? '' ) || hit.post_link || hit.permalink || hit.url || '#' );
			const a        = document.createElement( 'a' );
			a.className    = el.className || 'cta';
			a.dataset.element = id;
			a.textContent  = label;
			a.href         = postLink;
			el.replaceWith( a );

		} else {
			el.textContent = value;

			if ( tag === 'dd' ) {
				const iconType = content[ id + '_icon_type' ] || 'icon';
				const iconName = iconType === 'icon'  ? ( content[ id + '_icon_name'      ] || '' ) : '';
				const imageUrl = iconType === 'image' ? ( content[ id + '_icon_image_url' ] || '' ) : '';

				if ( iconName || imageUrl ) {
					const text = el.textContent;
					el.innerHTML = '';

					if ( iconName ) {
						const wrap = document.createElement( 'span' );
						wrap.className = 'tgbl-meta-icon';
						wrap.innerHTML = iconToSvg( iconName );
						el.appendChild( wrap );
					} else if ( imageUrl ) {
						const img = document.createElement( 'img' );
						img.src       = imageUrl;
						img.alt       = '';
						img.className = 'tgbl-meta-icon-img';
						el.appendChild( img );
					}

					const textEl = document.createElement( 'span' );
					textEl.textContent = text;
					el.appendChild( textEl );
				}
			}
		}
	} );

	// Hide the metadata dl when all its children are hidden.
	const meta = card.querySelector( '[data-element="metadata"]' );
	if ( meta ) {
		const anyVisible = Array.from( meta.querySelectorAll( '[data-element]' ) )
			.some( child => child.style.display !== 'none' );
		if ( ! anyVisible ) meta.style.display = 'none';
	}
}

/**
 * Renders a hit card using the managed slot-based template.
 *
 * Returns an htm/Preact element suitable for the InstantSearch `templates.item`
 * callback. The card HTML is injected via dangerouslySetInnerHTML to preserve
 * the full template markup (including CSS classes, data attributes, etc.).
 *
 * @param {Object}   hit          Algolia hit object.
 * @param {Object}   cardTemplate Parsed card_template JSON { template, content, styles }.
 * @param {string}   templateHtml Raw HTML of the template file.
 * @param {Function} html         InstantSearch htm tagged-template function.
 */
/**
 * For inline cards, wraps all non-hero direct children in a div.card-body so
 * the hero and content area can be laid out as a flex row without each content
 * element becoming its own horizontal flex item.
 */
function wrapInlineCardBody( card ) {
	const hero = card.querySelector( ':scope > .hero' );
	const body = document.createElement( 'div' );
	body.className = 'card-body';
	Array.from( card.children ).forEach( child => {
		if ( child !== hero ) body.appendChild( child );
	} );
	card.appendChild( body );
}

export function renderManagedCardTemplate( hit, cardTemplate, templateHtml, html, facetMappingsByAttribute = {} ) {
	const { content = {}, styles = {} } = cardTemplate;

	const root = getTemplateRoot( templateHtml );
	const card = root.cloneNode( true );

	fillCard( card, hit, content, facetMappingsByAttribute );

	if ( card.dataset.layout === 'inline' ) {
		wrapInlineCardBody( card );
	}

	const styleAttr = buildStyleAttr( styles );
	if ( styleAttr ) {
		card.setAttribute( 'style', ( card.getAttribute( 'style' ) || '' ) + styleAttr );
	}

	const rendered = card.outerHTML;

	return html`<div class="tgbl-ss-card-wrap" dangerouslySetInnerHTML=${{ __html: rendered }}></div>`;
}
